import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Flex } from "@jsq/ds/core";
import { Form } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { useEntityClientWizard } from "../context";
import { CaseInfoFields } from "./CaseInfoFields";
import { CaseInfoFooter } from "./CaseInfoFooter";
import { CaseInfoFormValues, createCaseInfoSchema } from "./schema";
import * as styles from "./styles_vanilla.css";
import { caseInfoValuesFromSearch } from "./utils";
import type { WizardCaseInfo } from "../types";

export const CaseInformationStep = () => {
  const { setCaseInfo, goNext, caseInfo, caseType, requestExit } = useEntityClientWizard();
  const { search } = useLocation();

  const schema = useMemo(() => createCaseInfoSchema(caseType), [caseType]);
  const fromSearch = useMemo(
    () => caseInfoValuesFromSearch(search, caseType),
    [search, caseType],
  );

  const initialValues: CaseInfoFormValues = useMemo(
    () => ({
      clientId: caseInfo?.clientId ?? fromSearch.clientId,
      clientName: caseInfo?.clientName ?? fromSearch.clientName,
      entityId: caseInfo?.entityId ?? fromSearch.entityId,
      entityName: caseInfo?.entityName ?? fromSearch.entityName,
      isUnlinked: caseInfo?.isUnlinked ?? false,
      programName: caseInfo?.programName ?? "",
      programId: caseInfo?.programId,
      assignToMyself: caseInfo?.assignToMyself ?? false,
    }),
    [caseInfo, fromSearch],
  );

  const handleSubmit = useCallback(
    (values: CaseInfoFormValues) => {
      const info: WizardCaseInfo = {
        clientId: values.clientId,
        clientName: values.clientName,
        entityId: values.entityId,
        entityName: values.entityName,
        isUnlinked: values.isUnlinked,
        programName: values.programName || undefined,
        programId: values.programId,
        assignToMyself: values.assignToMyself,
      };
      setCaseInfo(info);
      goNext();
    },
    [setCaseInfo, goNext],
  );

  return (
    <Form<CaseInfoFormValues>
      initialValues={initialValues}
      yupValidationSchema={schema}
      onSubmit={handleSubmit}
      submitOnlyChangedValues={false}
    >
      <Flex flexDirection="column" gap={Sizes.LG} className={styles.stepWrapper}>
        <CaseInfoFields />
      </Flex>
      <CaseInfoFooter onCancel={requestExit} />
    </Form>
  );
};
